import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Users,
  BookOpen,
  DollarSign,
  Star,
  Plus,
  Video,
  Calendar,
  LogOut,
  Home,
  Menu,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const TeacherDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({ classes: 0, students: 0 });
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateClass, setShowCreateClass] = useState(false);
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [newClass, setNewClass] = useState({
    title: '',
    description: '',
    sport: 'Chess',
    skill_level: 'beginner',
    price: '',
    duration: '',
    schedule: '',
    max_students: 8
  });
  const [newMeeting, setNewMeeting] = useState({
    topic: '',
    start_time: '',
    duration: 60,
    class_id: ''
  });

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, classesRes] = await Promise.all([
        axios.get(`${API}/dashboard/teacher-stats`),
        axios.get(`${API}/classes`)
      ]);
      setStats(statsRes.data);
      const myClasses = classesRes.data.filter(c => c.teacher_id === user?.id);
      setClasses(myClasses);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/classes`, {
        ...newClass,
        price: parseFloat(newClass.price),
        duration: parseInt(newClass.duration)
      });
      toast.success('Class created successfully!');
      setShowCreateClass(false);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create class');
    }
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/zoom/create-meeting`, newMeeting);
      if (response.data.success) {
        toast.success('Zoom meeting created successfully!');
        setShowCreateMeeting(false);
      } else {
        toast.error(response.data.message || 'Failed to create meeting');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create meeting');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="teacher-dashboard">
      <SEO title="Instructor Dashboard" description="Manage your classes and students at Sun Kamal Academy." />

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2 text-gray-700" aria-label="Open menu" data-testid="teacher-mobile-menu-toggle">
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-bold text-lg">Instructor</span>
        <div className="w-10" />
      </div>

      {sidebarOpen && <div className="md:hidden dashboard-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed md:sticky top-0 left-0 z-40 w-64 bg-white border-r min-h-screen transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <Link to="/" className="flex items-center gap-2">
                <img src="https://customer-assets.emergentagent.com/job_sunkamalplay/artifacts/oha9raa2_logo-kamal.webp" alt="Sun Kamal Academy" className="h-10 sm:h-12" />
              </Link>
              <button onClick={() => setSidebarOpen(false)} className="md:hidden p-1 text-gray-500" aria-label="Close menu">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="space-y-2">
              <div className="sidebar-link active" data-testid="nav-dashboard">
                <BookOpen className="w-5 h-5" />
                <span>My Classes</span>
              </div>
              <Link to="/" className="sidebar-link" onClick={() => setSidebarOpen(false)}>
                <Home className="w-5 h-5" />
                <span>Home</span>
              </Link>
              <button onClick={handleLogout} className="sidebar-link w-full text-left">
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 md:p-8 pt-16 md:pt-8">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="dashboard-title">Instructor Dashboard</h1>
            <p className="text-gray-600 text-sm sm:text-base">Manage your classes and students</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
            <Card className="stats-card" data-testid="stat-classes">
              <CardContent className="pt-4 sm:pt-6 pb-4 sm:pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl sm:text-3xl font-bold mb-1">{stats.classes}</div>
                    <div className="text-xs sm:text-sm opacity-90">Total Classes</div>
                  </div>
                  <BookOpen className="w-8 h-8 sm:w-12 sm:h-12 opacity-80" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white" data-testid="stat-students">
              <CardContent className="pt-4 sm:pt-6 pb-4 sm:pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl sm:text-3xl font-bold mb-1">{stats.students}</div>
                    <div className="text-xs sm:text-sm opacity-90">Total Students</div>
                  </div>
                  <Users className="w-8 h-8 sm:w-12 sm:h-12 opacity-80" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-purple-500 to-pink-600 text-white" data-testid="stat-earnings">
              <CardContent className="pt-4 sm:pt-6 pb-4 sm:pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl sm:text-3xl font-bold mb-1">$0</div>
                    <div className="text-xs sm:text-sm opacity-90">Earnings</div>
                  </div>
                  <DollarSign className="w-8 h-8 sm:w-12 sm:h-12 opacity-80" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white" data-testid="stat-rating">
              <CardContent className="pt-4 sm:pt-6 pb-4 sm:pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl sm:text-3xl font-bold mb-1">4.8</div>
                    <div className="text-xs sm:text-sm opacity-90">Avg Rating</div>
                  </div>
                  <Star className="w-8 h-8 sm:w-12 sm:h-12 opacity-80 fill-white" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="classes" className="w-full">
            <TabsList className="mb-4 sm:mb-6">
              <TabsTrigger value="classes" data-testid="tab-classes" className="text-xs sm:text-sm">My Classes</TabsTrigger>
              <TabsTrigger value="meetings" data-testid="tab-meetings" className="text-xs sm:text-sm">Zoom Meetings</TabsTrigger>
            </TabsList>

            <TabsContent value="classes">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold">My Classes</h2>
                <Dialog open={showCreateClass} onOpenChange={setShowCreateClass}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary text-sm" data-testid="create-class-btn">
                      <Plus className="w-4 h-4 mr-2" /> Create New Class
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
                    <DialogHeader>
                      <DialogTitle>Create New Class</DialogTitle>
                      <DialogDescription>Fill in the details to create a new class</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateClass} className="space-y-3 sm:space-y-4" data-testid="create-class-form">
                      <div>
                        <Label>Title</Label>
                        <Input
                          value={newClass.title}
                          onChange={(e) => setNewClass({ ...newClass, title: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={newClass.description}
                          onChange={(e) => setNewClass({ ...newClass, description: e.target.value })}
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <Label>Course Type</Label>
                          <Select value={newClass.sport} onValueChange={(value) => setNewClass({ ...newClass, sport: value })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Chess">Chess</SelectItem>
                              <SelectItem value="Drums">Tabla / Drums</SelectItem>
                              <SelectItem value="Yoga">Yoga</SelectItem>
                              <SelectItem value="Checkers">Checkers</SelectItem>
                              <SelectItem value="Art">Art & Craft</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Skill Level</Label>
                          <Select value={newClass.skill_level} onValueChange={(value) => setNewClass({ ...newClass, skill_level: value })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="beginner">Beginner</SelectItem>
                              <SelectItem value="intermediate">Intermediate</SelectItem>
                              <SelectItem value="advanced">Advanced</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <Label>Price ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newClass.price}
                            onChange={(e) => setNewClass({ ...newClass, price: e.target.value })}
                            required
                          />
                        </div>
                        <div>
                          <Label>Duration (minutes)</Label>
                          <Input
                            type="number"
                            value={newClass.duration}
                            onChange={(e) => setNewClass({ ...newClass, duration: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Schedule</Label>
                        <Input
                          placeholder="e.g., Mon & Wed, 6:00 PM"
                          value={newClass.schedule}
                          onChange={(e) => setNewClass({ ...newClass, schedule: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Max Students</Label>
                        <Input
                          type="number"
                          value={newClass.max_students}
                          onChange={(e) => setNewClass({ ...newClass, max_students: parseInt(e.target.value) })}
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full btn-primary">Create Class</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {loading ? (
                <div className="text-center py-12">Loading...</div>
              ) : classes.length === 0 ? (
                <Card className="p-8 sm:p-12 text-center">
                  <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg sm:text-xl font-bold mb-2">No classes created yet</h3>
                  <p className="text-gray-600 mb-4 text-sm sm:text-base">Create your first class to start teaching</p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-testid="classes-grid">
                  {classes.map((cls, index) => (
                    <Card key={cls.id} className="hover:shadow-lg transition">
                      <img src={cls.image} alt={cls.title} className="w-full h-32 sm:h-40 object-cover rounded-t-lg" loading="lazy" />
                      <CardContent className="p-4 sm:p-6">
                        <h3 className="text-base sm:text-lg font-bold mb-2">{cls.title}</h3>
                        <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">{cls.description}</p>
                        <div className="flex items-center justify-between text-xs sm:text-sm mb-3 sm:mb-4">
                          <div>
                            <span className="text-gray-600">Enrolled:</span>
                            <span className="font-medium ml-1">{cls.enrolled_count}/{cls.max_students}</span>
                          </div>
                          <div className="text-xl sm:text-2xl font-bold text-orange-600">${cls.price}</div>
                        </div>
                        <Button onClick={() => navigate(`/classes/${cls.id}`)} className="w-full btn-primary text-sm">
                          View Details
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="meetings">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold">Zoom Meetings</h2>
                <Dialog open={showCreateMeeting} onOpenChange={setShowCreateMeeting}>
                  <DialogTrigger asChild>
                    <Button className="zoom-button text-sm" data-testid="create-meeting-btn">
                      <Video className="w-4 h-4 mr-2" /> Schedule Meeting
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="mx-4">
                    <DialogHeader>
                      <DialogTitle>Schedule Zoom Meeting</DialogTitle>
                      <DialogDescription>Create a new Zoom session for your class</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateMeeting} className="space-y-3 sm:space-y-4" data-testid="create-meeting-form">
                      <div>
                        <Label>Meeting Topic</Label>
                        <Input
                          value={newMeeting.topic}
                          onChange={(e) => setNewMeeting({ ...newMeeting, topic: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Start Time</Label>
                        <Input
                          type="datetime-local"
                          value={newMeeting.start_time}
                          onChange={(e) => setNewMeeting({ ...newMeeting, start_time: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Duration (minutes)</Label>
                        <Input
                          type="number"
                          value={newMeeting.duration}
                          onChange={(e) => setNewMeeting({ ...newMeeting, duration: parseInt(e.target.value) })}
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full zoom-button">Create Meeting</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg sm:text-xl">Scheduled Meetings</CardTitle>
                  <CardDescription className="text-sm">Manage your upcoming Zoom sessions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-gray-600 text-sm">
                    No scheduled meetings. Create one to get started.
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
};

export default TeacherDashboard;
